import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { SESSIONS } from "../data/liveSessionData";

const LiveSessionStudent = () => {
  const urlCode = window.location.pathname.split("/")[2]?.toUpperCase() || "";

  const [code, setCode] = useState(urlCode);
  const [name, setName] = useState("");
  const [joined, setJoined] = useState(false);
  const [session, setSession] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [revealCounts, setRevealCounts] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Derived view — no phase state needed, everything comes from session + local state
  const getView = () => {
    if (!joined) return "join";
    if (!session) return "lobby";
    if (session.status === "ended") return "ended";
    if (session.status === "waiting") return "lobby";
    if (session.status === "slides") return "slide";
    if (session.status === "quiz") {
      if (session.current_question < 0) return "lobby";
      if (session.question_revealed) return "reveal";
      if (answeredQuestions[session.current_question] !== undefined) return "answered";
      return "question";
    }
    return "lobby";
  };

  // Fetch response breakdown when answer is revealed
  useEffect(() => {
    if (!session?.id || !session.question_revealed || session.current_question < 0) return;
    const fetch = async () => {
      const { data } = await supabase
        .from("session_responses")
        .select("answer")
        .eq("session_id", session.id)
        .eq("question_index", session.current_question);
      if (!data) return;
      const counts = {};
      data.forEach((r) => { counts[r.answer] = (counts[r.answer] || 0) + 1; });
      setRevealCounts({ questionIndex: session.current_question, total: data.length, counts });
    };
    fetch();
  }, [session?.id, session?.question_revealed, session?.current_question]);

  // Subscribe to session changes after joining
  useEffect(() => {
    if (!session?.id) return;
    const channel = supabase
      .channel(`student-session-${session.id}`)
      .on("postgres_changes", {
        event: "UPDATE", schema: "public", table: "live_sessions",
        filter: `id=eq.${session.id}`,
      }, (payload) => {
        setSession(payload.new);
        // Reset answer selection when a new question appears
        if (payload.new.current_question !== session.current_question && !payload.new.question_revealed) {
          setSelectedAnswer(null);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session?.id, session?.current_question]);

  const joinSession = async () => {
    if (!name.trim() || !code.trim()) return;
    setLoading(true);
    setError("");

    const { data: sessions, error: sErr } = await supabase
      .from("live_sessions")
      .select("*")
      .eq("code", code.toUpperCase())
      .neq("status", "ended")
      .single();

    if (sErr || !sessions) {
      setError("Session not found. Check the code and try again.");
      setLoading(false);
      return;
    }

    const { data: participant, error: pErr } = await supabase
      .from("session_participants")
      .insert({ session_id: sessions.id, name: name.trim() })
      .select()
      .single();

    if (pErr) {
      setError("Failed to join. Please try again.");
      setLoading(false);
      return;
    }

    setSession(sessions);
    setSessionData(SESSIONS[sessions.topic] || null);
    setParticipantId(participant.id);
    setJoined(true);
    setLoading(false);
  };

  const submitAnswer = async () => {
    if (!selectedAnswer || !session || session.question_revealed) return;
    const q = sessionData?.questions[session.current_question];
    if (!q) return;
    const isCorrect = selectedAnswer === q.correct;

    await supabase.from("session_responses").insert({
      session_id: session.id,
      participant_id: participantId,
      question_index: session.current_question,
      answer: selectedAnswer,
      is_correct: isCorrect,
    });

    setAnsweredQuestions((prev) => ({ ...prev, [session.current_question]: selectedAnswer }));
  };

  const view = getView();

  // ── JOIN ───────────────────────────────────────────────────────────────────
  if (view === "join") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🏥</div>
          <h1 className="text-3xl font-extrabold text-white">Join Session</h1>
          <p className="text-white/50 mt-1 text-sm">Enter the code shown on screen</p>
        </div>
        <div className="bg-white rounded-3xl p-6 shadow-2xl space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Session Code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCDEF"
              maxLength={6}
              className="w-full mt-1 text-center text-4xl font-extrabold tracking-[0.2em] text-indigo-700 border-2 border-indigo-200 rounded-xl p-3 focus:outline-none focus:border-indigo-500 font-mono uppercase"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Your Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && joinSession()}
              placeholder="Dr. Smith"
              className="w-full mt-1 border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-indigo-500 text-gray-800 font-semibold text-lg"
            />
          </div>
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <button onClick={joinSession} disabled={!name.trim() || !code.trim() || loading}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-extrabold text-lg disabled:opacity-40 hover:from-indigo-400 hover:to-purple-400 transition-all">
            {loading ? "Joining..." : "Join →"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── LOBBY ──────────────────────────────────────────────────────────────────
  if (view === "lobby") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="text-6xl mb-4 animate-pulse">⏳</div>
        <h2 className="text-2xl font-extrabold text-white mb-1">You're in!</h2>
        <p className="text-indigo-300 font-bold text-lg mb-1">{name}</p>
        <p className="text-white/50 text-sm">Waiting for the presenter to begin...</p>
        {sessionData && (
          <p className="text-white/40 mt-4 text-sm">{sessionData.emoji} {sessionData.topic}</p>
        )}
      </div>
    </div>
  );

  // ── SLIDE ──────────────────────────────────────────────────────────────────
  if (view === "slide" && sessionData) {
    const slide = sessionData.slides[session.current_slide];
    if (!slide) return null;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-lg mx-auto pt-4">
          <p className="text-center text-white/40 text-xs mb-4">
            {sessionData.emoji} {sessionData.topic} · Slide {session.current_slide + 1}
          </p>
          <div className="bg-white rounded-3xl p-6 shadow-2xl">
            <div className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2">{slide.subtitle}</div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-4">{slide.title}</h2>
            <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{slide.content}</div>
          </div>
        </div>
      </div>
    );
  }

  // ── QUESTION ───────────────────────────────────────────────────────────────
  if (view === "question" && sessionData) {
    const q = sessionData.questions[session.current_question];
    if (!q) return null;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4 flex flex-col">
        <div className="max-w-lg mx-auto w-full pt-4 flex flex-col flex-1">
          <p className="text-center text-white/40 text-xs mb-3">
            Question {session.current_question + 1} of {sessionData.questions.length}
          </p>
          <div className="bg-white rounded-3xl p-5 mb-4 shadow-2xl">
            <p className="font-extrabold text-gray-900 text-lg leading-snug">{q.question}</p>
          </div>
          <div className="space-y-3 flex-1">
            {q.options.map((opt) => (
              <button key={opt.id} onClick={() => setSelectedAnswer(opt.id)}
                className={`w-full rounded-2xl p-4 text-left font-bold text-base transition-all border-2 ${
                  selectedAnswer === opt.id
                    ? "border-indigo-500 bg-indigo-500 text-white shadow-lg scale-[1.02]"
                    : "border-gray-200 bg-white text-gray-800 hover:border-indigo-300 hover:bg-indigo-50"
                }`}>
                <span className="font-mono mr-2">{opt.id.toUpperCase()}.</span>{opt.text}
              </button>
            ))}
          </div>
          <div className="mt-5 pb-4">
            <button onClick={submitAnswer} disabled={!selectedAnswer}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-extrabold text-lg disabled:opacity-40 hover:from-green-400 hover:to-emerald-400 transition-all">
              Submit Answer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── ANSWERED (waiting for reveal) ──────────────────────────────────────────
  if (view === "answered") {
    const myAnswer = answeredQuestions[session?.current_question];
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-extrabold text-white mb-2">Answer submitted!</h2>
          <p className="text-indigo-300 font-semibold">You chose: <span className="font-bold">{myAnswer?.toUpperCase()}</span></p>
          <p className="text-white/40 mt-3 text-sm">Waiting for the presenter to reveal the answer...</p>
        </div>
      </div>
    );
  }

  // ── REVEAL ─────────────────────────────────────────────────────────────────
  if (view === "reveal" && sessionData) {
    const q = sessionData.questions[session.current_question];
    const myAnswer = answeredQuestions[session.current_question];
    const isCorrect = myAnswer === q.correct;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4 overflow-y-auto">
        <div className="max-w-lg mx-auto pt-4 pb-8">
          <div className={`rounded-3xl p-6 mb-4 text-center shadow-2xl border-4 ${isCorrect ? "bg-green-50 border-green-400" : myAnswer ? "bg-red-50 border-red-400" : "bg-amber-50 border-amber-400"}`}>
            <div className="text-5xl mb-3">{isCorrect ? "🎉" : myAnswer ? "😕" : "⏰"}</div>
            <h2 className={`text-2xl font-extrabold mb-1 ${isCorrect ? "text-green-700" : myAnswer ? "text-red-700" : "text-amber-700"}`}>
              {isCorrect ? "Correct!" : myAnswer ? "Not quite!" : "Time's up!"}
            </h2>
            {myAnswer && (
              <p className={`text-sm ${isCorrect ? "text-green-600" : "text-red-600"}`}>
                You answered: <strong>{myAnswer.toUpperCase()}</strong>
              </p>
            )}
          </div>

          <div className="bg-white rounded-3xl p-5 mb-4 shadow-xl">
            <p className="font-extrabold text-gray-900 mb-4 text-sm">{q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt) => {
                const count = revealCounts?.counts?.[opt.id] || 0;
                const total = revealCounts?.total || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const isCorrectOpt = opt.id === q.correct;
                const isMyWrongAnswer = opt.id === myAnswer && !isCorrect;
                return (
                  <div key={opt.id} className={`rounded-xl p-3 border-2 ${
                    isCorrectOpt ? "border-green-400 bg-green-50" :
                    isMyWrongAnswer ? "border-red-300 bg-red-50" :
                    "border-gray-100 bg-gray-50"
                  }`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`font-semibold text-sm ${isCorrectOpt ? "text-green-800" : isMyWrongAnswer ? "text-red-700" : "text-gray-600"}`}>
                        {isCorrectOpt ? "✅ " : isMyWrongAnswer ? "❌ " : ""}
                        {opt.id.toUpperCase()}. {opt.text}
                      </span>
                      <span className={`text-sm font-bold shrink-0 ml-2 ${isCorrectOpt ? "text-green-700" : "text-gray-400"}`}>
                        {pct}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all duration-500 ${isCorrectOpt ? "bg-green-500" : isMyWrongAnswer ? "bg-red-400" : "bg-gray-300"}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {revealCounts?.total > 0 && (
                <p className="text-xs text-gray-400 text-right pt-1">{revealCounts.total} response{revealCounts.total !== 1 ? "s" : ""}</p>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
            <div className="text-xs font-bold text-blue-500 mb-1">📖 Explanation</div>
            <p className="text-blue-900 text-sm leading-relaxed">{q.explanation}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── ENDED ──────────────────────────────────────────────────────────────────
  if (view === "ended") {
    const total = sessionData ? sessionData.questions.length : 0;
    const correct = Object.entries(answeredQuestions).filter(([idx, ans]) => {
      const q = sessionData?.questions[parseInt(idx)];
      return q && ans === q.correct;
    }).length;
    const answered = Object.keys(answeredQuestions).length;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-1">Well done, {name}!</h2>
          <p className="text-gray-500 mb-6 text-sm">{sessionData?.topic}</p>
          <div className="bg-indigo-50 rounded-2xl p-5 border-2 border-indigo-200 mb-6">
            <div className="text-5xl font-extrabold text-indigo-600">{correct} / {total}</div>
            <div className="text-sm text-indigo-700 font-semibold mt-1">Questions Correct</div>
            {answered < total && (
              <div className="text-xs text-gray-400 mt-1">{total - answered} not answered</div>
            )}
          </div>
          <p className="text-gray-400 text-sm">Thanks for participating! 👏</p>
        </div>
      </div>
    );
  }

  return null;
};

export default LiveSessionStudent;
